import { Dispatch } from '@reduxjs/toolkit'

import { MY_SHIFTS, ShiftsTab } from '@/constants'
import { getOpenShifts as getOpenShiftsApiCall, getPlannedShifts as getPlannedShiftsApiCall } from '@/helpers/api/shiftsApi'
import { createLatestRunGuard } from '@/helpers/createLatestRunGuard'
import { getEndOfWorkDay, getStartOfWorkDay } from '@/helpers/date'
import { getErrorResponse } from '@/helpers/getErrorResponse'
import { trans } from '@/helpers/trans'
import { Shift } from '@/models'
import {
    SET_LOADING,
    SHIFTS_FAILURE,
    SHIFTS_OPEN_SUCCESS,
    SHIFTS_PLANNED_SUCCESS,
    SHIFTS_REQUEST,
} from '@/redux/actionTypes'
import { Reducers } from '@/redux/reducers'

const startPlannedShiftsRun = createLatestRunGuard()
const startOpenShiftsRun = createLatestRunGuard()

const getSelectedPeriod = (state: Reducers): { startDate: Date, endDate: Date } => {
    const { selectedDays } = state.calendarReducer

    return {
        startDate: getStartOfWorkDay(selectedDays[0]),
        endDate: getEndOfWorkDay(selectedDays[selectedDays.length - 1]),
    }
}

export const getPlannedShifts = () => async (dispatch: Dispatch, getState: () => Reducers): Promise<void> => {
    const isLatestRun = startPlannedShiftsRun()

    dispatch({ type: SHIFTS_REQUEST })
    dispatch({ type: SET_LOADING, loading: true })

    try {
        const includes = [
            'department',
            'department.group',
            'tracks',
            'shiftPlannings',
            'shiftPlannings.user',
            'shiftPlannings.comments',
            'shiftPlannings.comments.owner',
        ]

        const { startDate, endDate } = getSelectedPeriod(getState())
        const response = await getPlannedShiftsApiCall(includes, startDate, endDate)

        if (! isLatestRun()) return

        dispatch({
            type: SHIFTS_PLANNED_SUCCESS,
            plannedShifts: response.data || [],
        })
    } catch (error) {
        if (! isLatestRun()) return

        dispatch({ type: SHIFTS_FAILURE, errors: [getErrorResponse(error)] })
    }

    dispatch({ type: SET_LOADING, loading: false })
}

export const getOpenShifts = () => async (dispatch: Dispatch, getState: () => Reducers): Promise<void> => {
    const isLatestRun = startOpenShiftsRun()

    dispatch({ type: SHIFTS_REQUEST })

    try {
        const includes = ['department', 'department.group']

        const { startDate, endDate } = getSelectedPeriod(getState())
        const response = await getOpenShiftsApiCall(includes, startDate, endDate)

        if (! isLatestRun()) return

        const openShifts = (response.data || [])
            .filter((shift: Shift) => shift.statusForCurrentUser !== 'full')

        dispatch({
            type: SHIFTS_OPEN_SUCCESS,
            openShifts,
        })
    } catch (error) {
        if (! isLatestRun()) return

        dispatch({ type: SHIFTS_FAILURE, errors: [getErrorResponse(error)] })
    }
}

export const getShiftsTabLink = (tab: ShiftsTab): string => {
    const tabLink = tab === MY_SHIFTS ? trans('nav.shifts.myShifts.link') : trans('nav.shifts.openShifts.link')

    return `/${trans('nav.shifts.link')}/${tabLink}`
}
